/**
 * UsernameSetupModal Component
 * Modal for setting up a username (required for social features)
 */

import { useCallback, useEffect, useState } from 'react';
import { setUsername as apiSetUsername, checkUsernameAvailability } from '../api';
import { useAuth } from '../contexts/AuthContext';
import ModalPortal from './ModalPortal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function UsernameSetupModal({ isOpen, onClose, onSuccess }: Props) {
  const { updateUser } = useAuth();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  const debouncedUsername = useDebounce(username, 300);

  // Check username availability when input changes
  const checkAvailability = useCallback(async (name: string) => {
    if (!name || name.length < 3) {
      setIsAvailable(null);
      return;
    }

    setChecking(true);
    try {
      const result = await checkUsernameAvailability(name);
      setIsAvailable(result.available);
      if (!result.available && result.error) {
        setError(result.error);
      } else {
        setError(null);
      }
    } catch {
      setIsAvailable(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedUsername) {
      checkAvailability(debouncedUsername);
    } else {
      setIsAvailable(null);
    }
  }, [debouncedUsername, checkAvailability]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = username.trim().toLowerCase();

    if (!trimmed) {
      setError('Please enter a username');
      return;
    }

    if (trimmed.length < 3 || trimmed.length > 20) {
      setError('Username must be 3-20 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const user = await apiSetUsername(trimmed);
      updateUser(user);
      setUsername('');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set username');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setUsername('');
      setError(null);
      setIsAvailable(null);
      onClose();
    }
  };

  const isValid = username.length >= 3 && username.length <= 20 && /^[a-zA-Z0-9_]+$/.test(username);
  const isSubmitDisabled = loading || !isValid || isAvailable === false;

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Choose a Username</h2>
            <button className="modal-close" onClick={handleClose} disabled={loading}>
              &times;
            </button>
          </div>

          <p className="modal-description">
            Choose a username to use social features like adding friends and sharing playlists.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">
                Username <span className="required">*</span>
              </label>
              <div className="input-with-status">
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) =>
                    setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
                  }
                  placeholder="e.g., movie_lover_42"
                  disabled={loading}
                  autoComplete="off"
                  maxLength={20}
                />
                {checking && <span className="input-status checking">Checking...</span>}
                {!checking && isAvailable === true && (
                  <span className="input-status available">Available</span>
                )}
                {!checking && isAvailable === false && (
                  <span className="input-status taken">Taken</span>
                )}
              </div>
              <p className="form-hint">3-20 characters. Letters, numbers, and underscores only.</p>
            </div>

            {error && <div className="form-error">{error}</div>}

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={handleClose} disabled={loading}>
                Skip for now
              </button>
              <button type="submit" disabled={isSubmitDisabled}>
                {loading ? 'Setting...' : 'Set Username'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
